import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Checkbox, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import { Form } from 'react-final-form';
import { Lead } from 'models/Lead';
import {
  getArrayLength,
  getHasElementsInArray,
  getIsArrayEmpty,
  getUserFullName,
  getCheckedValuesInObject,
} from 'utils';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './BroadcastPulseCheckModal.styles.scss';

const INPUT_NAME = 'leads';

type BroadcastPulseCheckModalProps = {
  className?: string;
  dealRoomId: string;
  leads: Partial<Lead>[];
  openBroadcastMessage?: () => void;
} & ModalProps;

const BroadcastPulseCheckModal: React.ForwardRefRenderFunction<
  ModalRef,
  BroadcastPulseCheckModalProps
> = (props, ref) => {
  const {
    className,
    dealRoomId,
    leads = [],
    openBroadcastMessage,
    ...rest
  } = props;

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('broadcast-pulse-check-modal', className);

  const leadsOptions = useMemo(
    () =>
      leads.map(({ _id, firstName, lastName }) => ({
        value: _id,
        label: getUserFullName(firstName, lastName),
      })),
    [leads],
  );

  const { t } = useTranslation();

  const changeForAll = useCallback(
    (isChecked: boolean) =>
      leads.reduce((acc, { _id }) => ({ ...acc, [_id]: isChecked }), {}),
    [leads],
  );

  const onSubmit = useCallback(
    async (values) => {
      const { leads = {} } = values || {};
      const leadIds = getCheckedValuesInObject(leads);

      try {
        await api.dealRoom.sendPulseCheck(dealRoomId, leadIds);
        popSuccess(t('broadcastPulseCheckSuccess'));
        modal?.close();
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, modal, popServerError, popSuccess, t],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('broadcastPulseCheck')}
      {...rest}
    >
      <Form
        onSubmit={onSubmit}
        render={({
          submitting,
          invalid,
          values: { [INPUT_NAME]: leadsValue },
          form: { change },
          handleSubmit,
        }) => {
          const leadsValueArray = Object.values(leadsValue || {});

          const isSelectedAll =
            getHasElementsInArray(leadsValueArray) &&
            getArrayLength(leadsValueArray) === getArrayLength(leads) &&
            leadsValueArray.every(Boolean);

          return (
            <form className={classes} onSubmit={handleSubmit}>
              {submitting && <Loader />}

              {getIsArrayEmpty(leads) ? (
                <p>{t('noActiveLeads')}</p>
              ) : (
                <>
                  <p>{t('broadcastPulseCheckDescription')}</p>

                  <Checkbox
                    checked={isSelectedAll}
                    label={t('selectAll')}
                    onChange={() =>
                      change(INPUT_NAME, changeForAll(!isSelectedAll))
                    }
                  />

                  <CheckboxFieldGroup
                    name={INPUT_NAME}
                    required
                    options={leadsOptions}
                  />
                </>
              )}

              <div className="actions">
                {openBroadcastMessage && (
                  <Button
                    variant="link"
                    className="open-other-modal"
                    onClick={() => {
                      modal?.close();
                      openBroadcastMessage();
                    }}
                  >
                    {t('broadcastMessageInstead')}
                  </Button>
                )}

                <Button variant="link" onClick={modal?.close}>
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={submitting || invalid}>
                  {t('send')}
                </Button>
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
};

export default forwardRef(BroadcastPulseCheckModal);
